import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
    Box,
    Typography,
} from '@mui/material';

import { Icon } from '@iconify/react';

import Header from '../../components/Header/Header';
import merchantColumns from './MerchantsColumn';
import AppTable from "../../components/AppTable/AppTable"

import './MerchantsPage.scss';
import FilterDropdown from '../../components/FilterDropdown/FilterDropdown';
import FilterSideModal from '../../components/FilterSideModal/FilterSideModal';
import merchantApi from '../../services/merchantApi';
import { createPortal } from 'react-dom';

const STATUS_OPTIONS = [
    {
        label: 'Active',
        value: 'active',
    },
    {
        label: 'Suspended',
        value: 'suspended',
    },
];

const DEFAULT_SUMMARY = {
    active: 0,
    outlets: 0,
    suspended: 0,
};

const DEFAULT_PAGINATION = {
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
    from: 0,
    to: 0,
};

const formatLastActive = (value) => {
    if (!value) return '-';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleString('en-MY', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const normalizeMerchantList = (response) => {
    const rows = Array.isArray(response?.data) ? response.data : [];
    const meta = response?.meta || DEFAULT_PAGINATION;
    const summary = response?.summary || {};

    return {
        rows: rows.map((merchant) => ({
            ...merchant,
            code: merchant.merchant_code,
            logo: merchant.logo_url,
            outlet_count: merchant.outlets_count ?? 0,
            last_active: formatLastActive(merchant.last_active_at),
        })),
        pagination: {
            ...DEFAULT_PAGINATION,
            ...meta,
        },
        summary: {
            active: summary.total_active_merchants ?? 0,
            outlets: summary.total_outlets ?? 0,
            suspended: summary.total_suspended_merchants ?? 0,
        },
    };
};

function MerchantNameTag({ merchant }) {
    return (
        <div className="merchant-modal__customer-card">
            {/* {
                merchant?.avatar ? <img src={merchant.avatar} alt={merchant.name} />
                    : <Avatar className="sr-table__mui-avatar" />
            } */}
            <div>
                <p className="merchant-modal__card-name">{merchant.name}</p>
            </div>
            <span className={`merchant-modal__card-status status-chip status-chip--${merchant.status}`}><Icon icon="ph:circle-fill" height={5} /> {merchant.status ? merchant.status[0].toUpperCase() + merchant.status.slice(1) : 'Unknown'}</span>
        </div>
    )
}

function SuspendModal({ merchant, onClose, onConfirm }) {
    const [confirming, setConfirming] = useState(false)

    const handleConfirm = async () => {
        if (confirming) return

        setConfirming(true)
        try {
            await onConfirm()
        } finally {
            setConfirming(false)
        }
    }

    return createPortal(
        <div className="merchant-modal-overlay" onClick={confirming ? undefined : onClose}>
            <div className="merchant-modal" onClick={(e) => e.stopPropagation()}>
                <div className="merchant-modal__header">
                    <h3>Suspend merchant account</h3>
                    {/* <button className="merchant-modal__close" onClick={onClose}><X size={20} /></button> */}
                </div>

                <div className="merchant-modal__body">
                    <p className="merchant-modal__suspend-desc">
                        This action will temporarily disable the merchant's account for this outlet only
                    </p>

                    <MerchantNameTag merchant={merchant} />

                    <ul className="merchant-modal__consequences">
                        <li>The merchant cannot access merchant merchant features.</li>
                        <li>The account can be reactivated later.</li>
                        <li>The action will be recorded for audit purposes.</li>
                    </ul>

                    {/* <div className="merchant-modal__field">
                        <label className="merchant-modal__label">Reason for suspension</label>
                        <textarea
                            className="merchant-modal__textarea"
                            rows={4}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                        />
                    </div> */}
                </div>

                <div className="merchant-modal__footer">
                    <button className="merchant-modal__cancel" disabled={confirming} onClick={onClose}>Cancel</button>
                    <button className="merchant-modal__confirm merchant-modal__confirm--danger" disabled={confirming} onClick={handleConfirm}>
                        <Icon className={confirming ? 'merchant-modal__spinner' : ''} icon={confirming ? 'ph:spinner-gap' : 'ph:prohibit'} height={18} />
                        {confirming ? 'Suspending...' : 'Suspend account'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    )
}

function ReactivateModal({ merchant, onClose, onConfirm }) {
    const [confirming, setConfirming] = useState(false)

    const handleConfirm = async () => {
        if (confirming) return

        setConfirming(true)
        try {
            await onConfirm()
        } finally {
            setConfirming(false)
        }
    }

    return createPortal(
        <div className="merchant-modal-overlay" onClick={confirming ? undefined : onClose}>
            <div className="merchant-modal" onClick={(e) => e.stopPropagation()}>
                <div className="merchant-modal__header">
                    <h3>Restore account access</h3>
                    {/* <button className="merchant-modal__close" onClick={onClose}><X size={20} /></button> */}
                </div>

                <div className="merchant-modal__body">
                    <p className="merchant-modal__suspend-desc">
                        This action will restore the merchant's full access to this outlet.
                    </p>

                    <MerchantNameTag merchant={merchant} />

                    <ul className="merchant-modal__consequences">
                        <li>Merchant can place orders again immediately.</li>
                        <li>Existing points and order history are unchanged.</li>
                        <li>Previously applied voucher restrictions are cleared.</li>
                    </ul>
                </div>

                <div className="merchant-modal__footer">
                    <button className="merchant-modal__cancel" disabled={confirming} onClick={onClose}>Cancel</button>
                    <button className="merchant-modal__confirm merchant-modal__confirm--primary" disabled={confirming} onClick={handleConfirm}>
                        <Icon className={confirming ? 'merchant-modal__spinner' : ''} icon={confirming ? 'ph:spinner-gap' : 'ph:check-circle'} height={18} />
                        {confirming ? 'Restoring...' : 'Restore access'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    )
}

function MerchantFilter({ open, setOpen, setValues, values, applyFilters }) {
    const filters = [
        {
            key: 'status',
            label: 'Status',
            type: 'chip',
            options: [
                { label: 'Active', value: 'active' },
                { label: 'Suspended', value: 'suspended' },
                { label: 'Pending', value: 'pending' },
            ],
        },
        {
            key: 'pos_system',
            label: 'POS System',
            type: 'chip',
            options: [
                { label: 'Lightspeed', value: 'lightspeed' },
                { label: 'Square', value: 'square' },
                { label: 'StoreHub', value: 'storehub' },
                { label: 'None', value: 'none' },
            ],
        },
        {
            key: 'monthly_gmv_range',
            label: 'Monthly GMV range',
            type: 'number-range',
            fromLabel: 'Min (RM)',
            toLabel: 'Max (RM)',
            fromPlaceholder: '0',
            toPlaceholder: '100,000',
        },
        {
            key: 'outlet_number_range',
            label: 'No. of outlet',
            type: 'number-range',
            fromLabel: 'Min',
            toLabel: 'Max',
            fromPlaceholder: '0',
            toPlaceholder: '100,000',
        },
    ]

    return (
        <FilterSideModal
            open={open}
            onClose={() => setOpen(false)}
            onApply={(vals) => {
                setOpen(false)
                applyFilters(vals)
            }}
            onReset={() => setValues({})}
            values={values}
            onChange={(key, val) => setValues((p) => ({ ...p, [key]: val }))}
            filters={filters}
        />
    )
}

export default function MerchantPage() {

    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [merchants, setMerchants] = useState([]);
    const [summary, setSummary] = useState(DEFAULT_SUMMARY);
    const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
    const [currentPage, setCurrentPage] = useState(1);
    const [filterValues, setFilterValues] = useState({ status: '' });
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedRow, setSelectedRow] = useState(null);
    const [suspendOpen, setSuspendOpen] = useState(false);
    const [reactiveOpen, setReactiveOpen] = useState(false);

    const [filterOpen, setFilterOpen] = useState(false)

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            loadMerchants();
        }, searchQuery ? 350 : 0);

        return () => window.clearTimeout(timeoutId);
    }, [currentPage, filterValues.status, searchQuery]);

    const loadMerchants = async () => {
        setLoading(true);
        setError('');

        try {
            const response = await merchantApi.merchants.list({
                status: filterValues.status,
                pos_system: filterValues.pos_system,
                gmv_min: filterValues.monthly_gmv_range?.from,
                gmv_max: filterValues.monthly_gmv_range?.to,
                outlet_min: filterValues.outlet_number_range?.from,
                outlet_max: filterValues.outlet_number_range?.to,
                search: searchQuery.trim(),
                page: currentPage,
                per_page: pagination.per_page,
            });
            const normalized = normalizeMerchantList(response);
            setMerchants(normalized.rows);
            setSummary(normalized.summary);
            setPagination(normalized.pagination);
        } catch (requestError) {
            setError(requestError?.message || 'Unable to load merchants.');
            setMerchants([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDirectToMerchantDetails = (merchantId) => {
        navigate(`/merchants/${merchantId}`);
    }

    const handleStatusChange = (row) => {
        setSelectedRow(row);
        if (row?.status === 'active') {
            setSuspendOpen(true);
        } else {
            setReactiveOpen(true);
        }
    }

    const handleSuspendConfirm = async () => {
        if (!selectedRow) return;
        setLoading(true);
        setError('');
        try {
            await merchantApi.merchants.updateStatus(selectedRow.id, { status: 'suspended' });
            await loadMerchants();
        } catch (err) {
            setError(err?.message || 'Unable to suspend merchant.');
        } finally {
            setLoading(false);
            setSuspendOpen(false);
            setSelectedRow(null);
        }
    }

    const handleReactivateConfirm = async () => {
        if (!selectedRow) return;
        setLoading(true);
        setError('');
        try {
            await merchantApi.merchants.updateStatus(selectedRow.id, { status: 'active' });
            await loadMerchants();
        } catch (err) {
            setError(err?.message || 'Unable to reactivate merchant.');
        } finally {
            setLoading(false);
            setReactiveOpen(false);
            setSelectedRow(null);
        }
    }

    const columns = merchantColumns({
        handleDirectToMerchantDetails,
        handleStatusChange,
    });

    console.log("merchants fitler", filterValues)
    return (
        <Box className="merchant-page">
            <Header
                items={[
                    { label: 'Cuscapi', to: '/' },
                    { label: 'Merchants', current: true },
                ]}
                title="Merchant Management"
                subtitle="Manage all merchants, subscriptions and system health."
            >

            </Header>
            <Box className="merchant-header">
                <Box >
                    <Typography className="title">
                        Merchant Management
                    </Typography>
                    <Typography className="subtitle">
                        Manage all merchants, subscriptions and system health.
                    </Typography>
                </Box>
                <Box className="merchant-table-toolbar">
                    <button className="merchant-btn" onClick={() => setFilterOpen(true)} >
                        <Icon icon="ph:funnel-simple" height={16} /> Filters
                    </button>
                    <button className="merchant-btn" type="button" title="Export API is not available">
                        <Icon icon="ph:arrow-line-down" height={16} /> Export CSV
                    </button>
                </Box>

            </Box>
            <Box className="merchant-summary">
                <Box className="summary-card">
                    <Typography className="label">
                        Active
                    </Typography>
                    <Typography className="value">
                        {summary.active}
                    </Typography>
                </Box>
                <Box className="summary-card">
                    <Typography className="label">
                        Total Outlets
                    </Typography>
                    <Typography className="value">
                        {summary.outlets}
                    </Typography>
                    <Typography className="subtitle">
                        across all merchants
                    </Typography>
                </Box>
                <Box className="summary-card">
                    <Typography className="label">
                        Suspended
                    </Typography>
                    <Typography className="value error">
                        {summary.suspended}
                    </Typography>
                    <Typography className="subtitle error">
                        payment overdue
                    </Typography>
                </Box>
            </Box>
            <MerchantFilter
                open={filterOpen}
                setOpen={setFilterOpen}
                values={filterValues}
                setValues={setFilterValues}
                applyFilters={(values) => {
                    console.log("apply filters", values)
                    setFilterValues(values);
                    setCurrentPage(currentPage === 1 ? '1' : 1);
                }}
            />
            {error && <div className="sr-error">{error}</div>}
            <AppTable
                loading={loading}
                columns={columns}
                rows={merchants}
                pagination={pagination}
                onPageChange={setCurrentPage}
                rowKey="id"
                toolBar={
                    <Box className="merchant-table-toolbar">
                        <FilterDropdown
                            label={'statuses'}
                            options={STATUS_OPTIONS}
                            value={filterValues.status}
                            onChange={(value) => {
                                setFilterValues({ ...filterValues, status: value });
                                setCurrentPage(currentPage === 1 ? '1' : 1);
                            }}
                        />
                        <Box className="toolbar-search">
                            <Icon icon="ph:magnifying-glass" height={18} />
                            <input
                                type="text"
                                placeholder="Search merchants, outlets"
                                value={searchQuery}
                                onChange={(event) => {
                                    setSearchQuery(event.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                        </Box>
                    </Box>
                }
                emptyText="No merchants found."
            />

            {
                suspendOpen && selectedRow &&
                <SuspendModal
                    merchant={selectedRow}
                    onClose={() => { setSuspendOpen(false); setSelectedRow(null) }}
                    onConfirm={handleSuspendConfirm}
                />
            }
            {
                reactiveOpen && selectedRow &&
                <ReactivateModal
                    merchant={selectedRow}
                    onClose={() => { setReactiveOpen(false); setSelectedRow(null) }}
                    onConfirm={handleReactivateConfirm}
                />
            }
        </Box>
    );
}
